import React from 'react';
import { useRouter } from 'next/router';
import { useLazyLoadQuery } from 'react-relay';
import { graphql } from 'relay-runtime';
import { useNoAccountModal } from '@sparknz/journeys-common';
import { trackCustomLink } from '@sparknz/tracking';

import { WearableEligibilityAccountSelectorQuery } from '__generated__/WearableEligibilityAccountSelectorQuery.graphql';
import { useProductDetails } from 'journey/productDetails/provider';
import { ProductDetailsActionTypes } from 'journey/productDetails/reducer/productDetails.reducer';
import {
  ActionLayerStatus,
  WearableEligibilityCheckSteps,
} from 'journey/productDetails/typings';
import { useCartCookie, useSegment } from 'shared/hooks';
import { extractFirstValue } from 'shared/utils';
import EligibilityAccountSelector from '../../../AddToCartSection/components/EligibilityAccountSelector';
import { useTrackAccountSelectorActionLayer } from '../../../AddToCartSection/components/IfpExistingPlanSelector/hooks';
import { useTrackEligibilitySelector } from '../hooks';

const wearableEligibilityAccountSelectorQuery = graphql`
  query WearableEligibilityAccountSelectorQuery(
    $cartId: String
    $channel: CartChannel!
    $offerId: String!
  ) {
    me {
      wearableEligibility(
        cartId: $cartId
        channel: $channel
        offerId: $offerId
      ) {
        accounts {
          accountNumber
          accountName
          isEligible
        }
        eligibleLines {
          accountNumber
          accountName
          lineNumber
          offerId
          offerName
        }
        ineligibleLines {
          accountNumber
          accountName
          lineNumber
          offerId
          offerName
          reason
        }
      }
    }
  }
`;

const WearableAccountSelector = () => {
  const { cartId } = useCartCookie();
  const { dispatch } = useProductDetails();
  const { channel } = useSegment();
  const router = useRouter();
  const planId = extractFirstValue(router.query.planId);
  const { openNoAccountModal } = useNoAccountModal();

  const {
    me: { wearableEligibility },
  } = useLazyLoadQuery<WearableEligibilityAccountSelectorQuery>(
    wearableEligibilityAccountSelectorQuery,
    {
      cartId: cartId || null,
      channel,
      offerId: planId,
    },
    {
      fetchPolicy: 'network-only',
    },
  );

  const { accounts, eligibleLines, ineligibleLines } = wearableEligibility;

  useTrackAccountSelectorActionLayer();
  useTrackEligibilitySelector();

  const handleNoAccountsWearables = () => {
    dispatch({
      type: ProductDetailsActionTypes.SetActionLayer,
      actionLayerStatus: ActionLayerStatus.Close,
    });

    openNoAccountModal({
      onContinue() {
        dispatch({
          type: ProductDetailsActionTypes.SetActionLayer,
          actionLayerStatus: ActionLayerStatus.ShowNewPlan,
          wearableEligibility: {
            activeStep: WearableEligibilityCheckSteps.AccountSelection,
          },
        });
      },
      hideCheckout: true,
    });
  };

  const handleAccountSelection = (accountNumber: string | null) => {
    dispatch({
      type: ProductDetailsActionTypes.SetWearableEligibility,
      wearableEligibility: {
        activeStep: WearableEligibilityCheckSteps.LineSelection,
        selectedAccountNumber: accountNumber,
        eligibility: {
          ...wearableEligibility,
          eligibleLines: eligibleLines?.filter(
            (line) => line.accountNumber === accountNumber,
          ),
          ineligibleLines: ineligibleLines?.filter(
            (line) => line.accountNumber === accountNumber,
          ),
        },
      },
    });

    trackCustomLink('Continue');
  };

  return (
    <EligibilityAccountSelector
      accounts={accounts}
      onAccountSelection={handleAccountSelection}
      handleNoAccounts={handleNoAccountsWearables}
    />
  );
};

export default WearableAccountSelector;
